import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedSubscribable } from '@benzinga/subscribable';

import { CoinData, CoinGeckoId, HowToBuyArticleData, PollData, VoteData, VoteParams } from './entities';
import { CryptoRestful } from './restful';
import { Session } from '@benzinga/session';

interface ErrorEvent {
  error: SafeError;
  errorType:
    | 'get_coin_error'
    | 'get_coins_error'
    | 'get_how_to_buy_article_error'
    | 'get_number_of_votes_error'
    | 'post_vote_error';
  type: 'error';
}

interface CoinsEvent {
  coins: CoinData[];
  type: 'get_coins';
}

interface CoinEvent {
  coin: CoinData;
  type: 'get_coin';
}

interface ArticleEvent {
  article: HowToBuyArticleData;
  type: 'get_how_to_buy_article';
}

interface PollEvent {
  poll: PollData | VoteData;
  type: 'get_number_of_votes' | 'post_vote';
}

export type CryptoRestfulEvent = ErrorEvent | CoinEvent | CoinsEvent | PollEvent | ArticleEvent;

interface CryptoFunctions {
  getCoin: CryptoRequest['getCoin'];
  getCoins: CryptoRequest['getCoins'];
}

export class CryptoRequest extends ExtendedSubscribable<CryptoRestfulEvent, CryptoFunctions> {
  private restful: CryptoRestful;

  constructor(session: Session) {
    super();
    this.restful = new CryptoRestful(session);
  }

  public getCoin = async (symbol: string): SafePromise<CoinData> => {
    const response = await this.restful.getCoin(symbol);
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_coin_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        coin: response.ok,
        type: 'get_coin',
      });
    }
    return response;
  };

  public getCoins = async (symbols: string[]): SafePromise<CoinData[]> => {
    const stringifiedSymbols = symbols.join(',');
    const response = await this.restful.getCoins(stringifiedSymbols);
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_coins_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        coins: response.ok,
        type: 'get_coins',
      });
    }
    return response;
  };

  getHowToBuyArticle = async (symbol: string): SafePromise<HowToBuyArticleData> => {
    const response = await this.restful.getHowToBuyArticle(symbol);
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_how_to_buy_article_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        article: response.ok,
        type: 'get_how_to_buy_article',
      });
    }
    return response;
  };

  getNumberOfVotes = async (coinId: CoinGeckoId): SafePromise<PollData> => {
    const response = await this.restful.getNumberOfVotes(coinId);
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_number_of_votes_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        poll: response.ok,
        type: 'get_number_of_votes',
      });
    }
    return response;
  };

  postMOBVote = async (postData: VoteParams): SafePromise<VoteData> => {
    const response = await this.restful.postMOBVote(postData);
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'post_vote_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        poll: response.ok,
        type: 'post_vote',
      });
    }
    return response;
  };

  public onSubscribe = (): CryptoFunctions => ({
    getCoin: this.getCoin,
    getCoins: this.getCoins,
  });
}
